import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { canManageCourierRounds } from "../../utils/helper";
import CourierListPage, { CourierListHeadAction } from "./courier-list-page";
import CreateRoundForm from "./forms/create-round-form";
import AddRoundStopForm from "./forms/add-round-stop-form";
import Confirmation from "../commons/confirmation";
import CourierTable from "./ui/courier-table";
import CourierBooleanTag from "./ui/courier-boolean-tag";
import RoundStopsModal from "./ui/round-stops-modal";
import { useCourierMutationDone } from "./ui/use-courier-mutation-done";

const RoundsList = props => {
  const {
    getCourierRounds,
    rounds = [],
    courierLoading,
    createCourierRound,
    deleteCourierRound,
    getCourierRoundStops,
    roundStops = [],
    createCourierRoundStop,
    deleteCourierRoundStop,
    reorderCourierRoundStops,
    optimizeRoundStops,
    getCourierZones,
    zones = [],
    getCourierCenters,
    centers = [],
    resetCourierState,
    courierApiError
  } = props;

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [isOpenStopModal, setIsOpenStopModal] = useState(false);
  const [deleteRoundTarget, setDeleteRoundTarget] = useState(null);
  const [deleteStopTarget, setDeleteStopTarget] = useState(null);
  const canManage = canManageCourierRounds();

  useCourierMutationDone(courierLoading, courierApiError, () => {
    setIsOpenCreateModal(false);
    setIsOpenStopModal(false);
  });

  useEffect(() => {
    getCourierRounds({});
    getCourierZones && getCourierZones({});
    getCourierCenters && getCourierCenters({});
    return () => {
      resetCourierState();
    };
  }, []);

  const openRound = round => {
    setSelectedRound(round);
    getCourierRoundStops(round.id);
  };

  const moveStop = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= roundStops.length) return;
    const ids = roundStops.map(s => s.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderCourierRoundStops({ roundId: selectedRound.id, stopIds: ids });
  };

  const usedCenterIds = new Set(roundStops.map(s => s.center));
  const availableCenters = centers.filter(
    c =>
      (!selectedRound || c.zone === selectedRound.zone) &&
      !usedCenterIds.has(c.id)
  );
  const nextSequence =
    (roundStops.reduce((max, s) => Math.max(max, s.sequence || 0), 0) || 0) +
    1;

  return (
    <CourierListPage
      title="Courier Rounds"
      count={rounds.length}
      loading={courierLoading}
      headActions={
        canManage && (
          <CourierListHeadAction
            label="Add Round"
            onClick={() => setIsOpenCreateModal(true)}
          />
        )
      }
    >
      <CourierTable>
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Zone</th>
            <th>Stops</th>
            <th className="status">Active</th>
            <th className="act">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map(r => (
            <tr key={r.id}>
              <td>
                {r.code}
                <span className="sm-text">ID {r.id}</span>
              </td>
              <td>{r.name}</td>
              <td>{r.zoneName}</td>
              <td>{r.stopsCount}</td>
              <td className="status">
                <CourierBooleanTag value={r.isActive} />
              </td>
              <td className="act">
                <span className="courier-row-actions">
                  <a
                    title="Stops"
                    className="action icon-edit"
                    href=" "
                    onClick={e => {
                      e.preventDefault();
                      openRound(r);
                    }}
                  />
                  {canManage && (
                    <a
                      title="Delete"
                      className="action icon-cancel"
                      href=" "
                      onClick={e => {
                        e.preventDefault();
                        setDeleteRoundTarget(r);
                      }}
                    />
                  )}
                </span>
              </td>
            </tr>
          ))}
          {!rounds.length && !courierLoading && (
            <tr>
              <td colSpan={6}>No rounds found.</td>
            </tr>
          )}
        </tbody>
      </CourierTable>

      {selectedRound && (
        <Modal
          isOpen={!!selectedRound}
          onRequestClose={() => setSelectedRound(null)}
          className="courier-wide-modal"
        >
          <RoundStopsModal
            round={selectedRound}
            stops={roundStops}
            canManage={canManage}
            onClose={() => setSelectedRound(null)}
            onAddStop={() => setIsOpenStopModal(true)}
            onOptimize={() => optimizeRoundStops(selectedRound.id)}
            onMoveStop={moveStop}
            onRemoveStop={setDeleteStopTarget}
          />
        </Modal>
      )}

      {isOpenCreateModal && (
        <Modal
          isOpen={isOpenCreateModal}
          onRequestClose={() => setIsOpenCreateModal(false)}
          className="small-modal"
        >
          <CreateRoundForm
            closeModal={() => setIsOpenCreateModal(false)}
            loading={courierLoading}
            zones={zones}
            apiError={courierApiError}
            createCourierRound={createCourierRound}
          />
        </Modal>
      )}

      {isOpenStopModal && selectedRound && (
        <Modal
          isOpen={isOpenStopModal}
          onRequestClose={() => setIsOpenStopModal(false)}
          className="small-modal"
        >
          <AddRoundStopForm
            closeModal={() => setIsOpenStopModal(false)}
            loading={courierLoading}
            centers={availableCenters}
            roundId={selectedRound.id}
            nextSequence={nextSequence}
            apiError={courierApiError}
            createCourierRoundStop={createCourierRoundStop}
          />
        </Modal>
      )}

      {deleteRoundTarget && (
        <Modal
          isOpen={!!deleteRoundTarget}
          onRequestClose={() => setDeleteRoundTarget(null)}
          className="small-modal"
        >
          <Confirmation
            text={`Delete round "${deleteRoundTarget.code}"?`}
            onConfirm={() => {
              deleteCourierRound(deleteRoundTarget.id);
              setDeleteRoundTarget(null);
            }}
            onClose={() => setDeleteRoundTarget(null)}
          />
        </Modal>
      )}

      {deleteStopTarget && selectedRound && (
        <Modal
          isOpen={!!deleteStopTarget}
          onRequestClose={() => setDeleteStopTarget(null)}
          className="small-modal"
        >
          <Confirmation
            text={`Remove stop "${deleteStopTarget.centerName}"?`}
            onConfirm={() => {
              deleteCourierRoundStop({
                roundId: selectedRound.id,
                id: deleteStopTarget.id
              });
              setDeleteStopTarget(null);
            }}
            onClose={() => setDeleteStopTarget(null)}
          />
        </Modal>
      )}
    </CourierListPage>
  );
};

export default RoundsList;
